import { CompanyGigShift, GigType, PaymentMethod } from "model/Gig";
import { Setting } from "model/Workgroup";

import type {
  CreateGigType,
  FormData,
  RecruitFormData,
  SelectedWorker,
} from "./gigFormModal.types";

/**
 * Returns true when the gig type belongs to a recruit-capable public/team flow.
 */
export const isPublicOrTeamType = (type?: GigType): boolean => {
  return (
    type === GigType.PUBLIC ||
    type === GigType.AUTOGENERATED ||
    type === GigType.HIDDEN ||
    type === GigType.STAFFING ||
    type === GigType.WORKPOOL
  );
};

export const toCreateGigType = (type?: GigType): CreateGigType => {
  if (type === GigType.PUBLIC) {
    return GigType.PUBLIC;
  }

  if (type === GigType.AUTOGENERATED || type === GigType.WORKPOOL) {
    return GigType.WORKPOOL;
  }

  return GigType.SOLO;
};

/**
 * Returns true when a company setting key exists and is enabled ("true").
 */
export const hasEnabledModule = (
  companySettings: Setting[] | undefined,
  key: string,
): boolean => {
  return (
    companySettings?.find((setting: Setting) => setting.key === key)?.value ===
    "true"
  );
};

/**
 * Builds the initial schedule form state from existing gig data or fallback inputs.
 */
export const buildInitialFormData = (
  shifts: CompanyGigShift[] | undefined,
  type: GigType | undefined,
): FormData => {
  return {
    description: "",
    shifts: shifts ?? [],
    type: toCreateGigType(type),
  };
};

/**
 * Builds the initial recruit form state from existing gig data.
 */
export const buildInitialRecruitFormData = (): RecruitFormData => {
  return {
    skills: [],
    amountOfWorkers: 1,
    paymentType: PaymentMethod.NONE,
    workGroupId: undefined,
    role: "",
    maxRate: 170,
    minRate: 140,
  };
};

/**
 * Serializes current schedule form state plus worker selections for dirty checking.
 */
export const buildScheduleSnapshot = (
  formData: FormData,
  selectedWorkers: SelectedWorker[],
): string => {
  return JSON.stringify({
    ...formData,
    selectedWorkers: selectedWorkers.map((worker) => ({
      id: worker.id,
      workerId: worker.workerId,
      selectedTeamId: worker.selectedTeamId ?? null,
      selectedCustomerProduct: worker.selectedCustomerProduct ?? null,
    })),
  });
};

/**
 * Serializes the initial schedule form state for dirty checking baseline comparisons.
 */
export const buildInitialScheduleSnapshot = (
  initialFormData: FormData,
): string => {
  return JSON.stringify({
    ...initialFormData,
    selectedWorkers: [],
  });
};
